import type { Schema } from '@/services/api/schemas'
import type { EntityFormState, PatternFormState, RelationFormState } from './types'

/**
 * Fork-on-edit decision + pending-edit descriptors (ADR-0005, SC-003).
 *
 * Editing a *global* schema from the case view forks it into a case-owned copy
 * with the triggering edit applied. These pure helpers decide whether to fork
 * and package the edit for the fork endpoint; the IO lives in `useSchemaManager`.
 */

export type SchemaEditKind = 'entity' | 'relation' | 'pattern'
export type SchemaEditOp = 'create' | 'update' | 'delete'

export interface PendingSchemaEdit {
  kind: SchemaEditKind
  op: SchemaEditOp
  /** The GLOBAL child id the UI was viewing — for update/delete. */
  element_id?: string
  /** Raw form payload — for create/update. */
  data?: EntityFormState | RelationFormState | PatternFormState
}

/** True when we're in a case context viewing a *global* schema, so the next
 * edit must fork rather than mutate the shared schema in place. */
export function shouldFork(caseId: string | undefined, schema: Schema | null): boolean {
  return Boolean(caseId) && schema != null && schema.case_id == null
}

/** The editable default fork name, `"<case> - <orig>"`. */
export function forkDefaultName(caseName: string | undefined, schemaName: string): string {
  const prefix = caseName?.trim() || 'Case'
  return `${prefix} - ${schemaName}`
}

/** A create (no id) or update (id) edit carrying the form payload. */
export function saveEdit(
  kind: SchemaEditKind,
  data: EntityFormState | RelationFormState | PatternFormState,
  editingId?: string
): PendingSchemaEdit {
  return editingId
    ? { kind, op: 'update', element_id: editingId, data }
    : { kind, op: 'create', data }
}

/** A delete edit targeting the global child id. */
export function deleteEdit(kind: SchemaEditKind, elementId: string): PendingSchemaEdit {
  return { kind, op: 'delete', element_id: elementId }
}
